import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import Handlebars from '@sparta.global.vendors/handlebars';
import PictureDefaultsUtility from '@sparta.global.utilities/picture-defaults-utility/dist/es5.js';
var _PictureDefaultsUtili = PictureDefaultsUtility.getPictureConstant(),
  LAZY_LOAD_CLASS = _PictureDefaultsUtili.LAZY_LOAD_CLASS,
  DEFAULT_PLACEHOLDER = _PictureDefaultsUtili.DEFAULT_PLACEHOLDER;
var viewports = PictureDefaultsUtility.getPictureViewports();
var mediaQueriesList = PictureDefaultsUtility.getPictureMediaQList();

/**
 * @method generateSourceString
 * @param {Object} source single image source from JCR
 * @param {String} viewport name of current viewport size
 * @param {Boolean} isLazyLoad - coming from JCR
 * @param {String} blurrySrc - blurry image
 * @param {Boolean} custom is this a custom viewport?
 * @description Creates the `<source>` tag for a `<picture>` source based on JCR data
 * @returns {String | Boolean} `<source>` as string or false if no custom or source.media
 */
function generateSourceString(source, viewport, isLazyLoad, blurrySrc, custom) {
  // default custom to false
  // TODO: replace with default parameter value when transpilation works
  if (typeof custom === 'undefined') {
    custom = false; // eslint-disable-line no-param-reassign
  }

  // if this is a custom viewport and no media declaration was provided, exit
  if (custom && !source.media) {
    return false;
  }

  // empty string to append our attributes to
  var attributes = '';

  // check for a supplied media attribute, or use the default media for that viewport
  var media = "media=\"".concat(source.media ? source.media : mediaQueriesList[viewport], "\"");

  // iterate through defined attributes for this source
  // TODO: replace for...in & Object.hasOwnProperty() with for...of when transpiling works
  Object.keys(source).forEach(function (attr) {
    // if this isn't srcset or media or blurry image, just create the attrbitue string
    if (attr !== 'srcset' && attr !== 'media' && attr !== 'blurry' && !blurrySrc) {
      attributes += " ".concat(attr, "=\"").concat(source[attr], "\"");
      // we need to skip the blurry object property
    } else if (attr !== 'blurry' && (attr === 'srcset' || blurrySrc)) {
      // if blurry image is absent for lazy loading,
      // we need to set it to the default placeholder image
      var blurryImage = !blurrySrc && isLazyLoad ? DEFAULT_PLACEHOLDER : blurrySrc;
      // set srcset to blurry image if exists
      var srcset = isLazyLoad ? " srcset=\"".concat(blurryImage, "\" data-src=\"").concat(source.srcset.join(','), "\" ").concat(attr, "=\"").concat(source[attr], "\"") : " srcset=\"".concat(source.srcset.join(','), "\" ").concat(attr !== "srcset" ? "".concat(attr, "=\"").concat(source[attr], "\"") : "");
      attributes += srcset;
    }
  });
  return "<source ".concat(media).concat(attributes, ">");
}

/**
 * @method generateSourceTags
 * @param {Object} sources object of sources from JCR
 * @description Creates an array of strings representing the `<source>` elements generated by this
 * helper
 * @returns {Array} array of source strings
 */
function generateSourceTags(sources) {
  // initialize empty array to store each source for this `<picture>`
  var sourceStrings = [];
  var imageAttrs = sources.imageAttributes;
  // set to true if lazyLoad is true or lazy load class exists
  var isLazyLoad = sources.lazyLoad || imageAttrs && imageAttrs.class && imageAttrs.class.indexOf(LAZY_LOAD_CLASS) !== -1;

  // iterate through viewports
  viewports.forEach(function itertateThroughViewports(viewport) {
    // if this viewport was provided in our JCR data
    if (sources[viewport]) {
      var blurry = '';
      if (sources[viewport] instanceof Array) {
        // get blurry image from viewport array
        var blurryImage = sources[viewport].filter(function (value) {
          return value.blurry;
        });
        if (blurryImage[0] && blurryImage[0].blurry) {
          blurry = blurryImage[0].blurry;
        }
      }
      // check for custom viewport declarations first
      if (viewport === 'custom') {
        // iterate through custom srcsets and generate `<source>` tags
        sources[viewport].forEach(function iterateThroughCustomViewports(customViewport) {
          // skip blurry property
          if (!customViewport.blurry) {
            sourceStrings.push(generateSourceString(customViewport, viewport, isLazyLoad, blurry, true));
          }
        });
      } else if (sources[viewport] instanceof Array) {
        // Sort since type image/webp needs to be first in the source lists.
        sources[viewport].sort(function sortWebpFirst(a) {
          return a.type && a.type === 'image/webp' ? -1 : 1;
        }).forEach(function iterateThroughViewports(sourceViewport) {
          if (!sourceViewport.blurry) {
            sourceStrings.push(generateSourceString(sourceViewport, viewport, isLazyLoad, blurry));
          }
        });
      } else if (sources[viewport] instanceof Object) {
        sourceStrings.push(generateSourceString(sources[viewport], viewport, isLazyLoad, sources[viewport].blurry));
      }
    }
  });
  return sourceStrings;
}

/**
 * @method generateAttributes
 * @param {Object} attrs key value pair for attributes
 * @param {Boolean} isLazyLoad - coming from JCR
 * @returns {String} combined attributes
 */
function generateAttributes(attrs, isLazyLoad) {
  var combinedAttrs = '';
  var attributes = {};

  // add lazy load class to attributes when no other classes are coming from JCR
  if (isLazyLoad) {
    Object.assign(attrs, {
      class: LAZY_LOAD_CLASS
    });
  }
  attributes = _objectSpread({}, attrs);
  Object.keys(attributes).forEach(function (key) {
    var attrValue = attributes[key].toString();
    var noLazyClass = attrValue && attrValue.indexOf(LAZY_LOAD_CLASS) === -1;
    // add lazy load class to the classes coming from JCR and combine all attributes
    combinedAttrs += key === 'class' && isLazyLoad && noLazyClass ? "".concat(key, "=\"").concat(attrValue, " ").concat(LAZY_LOAD_CLASS, "\" ") : combinedAttrs += "".concat(key, "=\"").concat(attrValue, "\" ");
  });
  return combinedAttrs;
}

/**
 * Generates the image tag used in the picture element
 *
 * @method generateImageTag
 * @param {Object} sources - object of sources from JCR
 * @returns {String} default or blurry image with combine attributes
 */
function generateImageTag(sources) {
  var attributes = sources.imageAttributes || {};
  var isLazyLoad = sources.lazyLoad;

  // set attribute to not load images if `skipSlow` is set to true in JCR
  var skipSlow = sources.skipSlow;
  var skipLoadingOnSlowNetwork = skipSlow ? ' data-skip-slow="true"' : '';

  // @todo deprecate alt property for the imageAttributes
  // this is backwards compatibility to move old alt prop into the imageAttributes.
  if (!attributes.alt) {
    Object.assign(attributes, {
      alt: sources.alt || ''
    });
  }
  var attrs = generateAttributes(attributes, isLazyLoad);
  return "<img src=\"".concat(DEFAULT_PLACEHOLDER, "\" ").concat(attrs).concat(skipLoadingOnSlowNetwork, "/>");
}

/**
   * @method helpers.picture
   * @param {Object} sources object of image sources for `<picture>` element
   * @returns {Node} Handlebars.SafeString() of `<picture>` element
   */
function picture(sources) {
  if (sources) {
    var tag = '<picture>';
    var closingTag = '</picture>';
    var fallbackImg = generateImageTag(sources);

    /* eslint-disable max-len */
    /*
    // DE50454 - Uncomment 134-139 to test
    const sourceTags = [
      `<source srcset="${large.src}" media="${mediaQueryUtility.get({ type: 'viewport', name: 'large' })}">`
      `<source srcset="${medium.src}" media="media="${mediaQueryUtility.get({ type: 'viewport', name: 'medium' })}">`
      `<source srcset="${small.src}" media="media="${mediaQueryUtility.get({ type: 'viewport', name: 'small' })}">`
    ];*/
    /* eslint-enable max-len */

    // DE50454 - Comment out 142 to test
    var sourceTags = generateSourceTags(sources);
    return new Handlebars.SafeString("".concat(tag).concat(sourceTags.join('')).concat(fallbackImg).concat(closingTag));
  }
  throw new Error('No image sources provided to picture helper.');
}

/**
 * @class pictureHelper
 * @description Class used to register our helper with Handlebars
 */

export { picture };